import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from 'react'

/**
 * Tailwind CSS breakpoint names
 * Standard breakpoints aligned with Tailwind's default theme.
 */
type Breakpoint = 'sm' | 'md' | 'lg' | 'xl' | '2xl'
type ExtendedBreakpoint = Breakpoint | 'xs'

/**
 * Standard Tailwind CSS breakpoint values (in pixels).
 * These are hardcoded because Tailwind v4 does not reliably expose
 * breakpoints as CSS variables, and these values are stable.
 * @see https://tailwindcss.com/docs/responsive-design
 */
const BREAKPOINTS: Record<Breakpoint, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const

function breakpointForWidth(width: number) {
  if (width >= BREAKPOINTS['2xl']) return '2xl'
  if (width >= BREAKPOINTS.xl) return 'xl'
  if (width >= BREAKPOINTS.lg) return 'lg'
  if (width >= BREAKPOINTS.md) return 'md'
  if (width >= BREAKPOINTS.sm) return 'sm'
  return 'xs'
}

// Built once per breakpoint so the returned object and functions stay stable for memo deps.
function responsiveResult(breakpoint: ExtendedBreakpoint) {
  return {
    breakpoint,
    isXs: breakpoint === 'xs',
    isSm: breakpoint === 'sm',
    isMd: breakpoint === 'md',
    isLg: breakpoint === 'lg',
    isXl: breakpoint === 'xl',
    is2Xl: breakpoint === '2xl',
    isAtLeast: (bp: Breakpoint) => breakpoint !== 'xs' && BREAKPOINTS[breakpoint] >= BREAKPOINTS[bp],
    isLessThan: (bp: Breakpoint) => breakpoint === 'xs' || BREAKPOINTS[breakpoint] < BREAKPOINTS[bp],
  }
}

const responsiveResults = {
  xs: responsiveResult('xs'),
  sm: responsiveResult('sm'),
  md: responsiveResult('md'),
  lg: responsiveResult('lg'),
  xl: responsiveResult('xl'),
  '2xl': responsiveResult('2xl'),
}

function subscribeToWindow(onChange: () => void) {
  const queries = Object.values(BREAKPOINTS).map(width => window.matchMedia(`(min-width: ${width}px)`))
  for (const query of queries) query.addEventListener('change', onChange)
  return () => {
    for (const query of queries) query.removeEventListener('change', onChange)
  }
}

function subscribeToNothing() {
  return () => {}
}

function getWindowBreakpoint() {
  return breakpointForWidth(window.innerWidth)
}

function getServerBreakpoint() {
  return '2xl' as const
}

/**
 * The breakpoint `useResponsive` returns instead of the window's, for content
 * laid out in a narrower area, such as a page beside a side panel. `null`
 * means follow the window. Context crosses portals, so portalled content
 * (dialogs, menus, tooltips) sits in `FollowWindowBreakpoint`: it renders
 * in `body`, and its CSS follows the window.
 */
export const ResponsiveBreakpointContext = createContext<ExtendedBreakpoint | null>(null)

/** Makes `useResponsive` inside follow the window, for portalled content. */
export function FollowWindowBreakpoint({ children }: { children: ReactNode }) {
  return createElement(ResponsiveBreakpointContext.Provider, { value: null }, children)
}

/**
 * The breakpoint of an element's width, from one `ResizeObserver` that sets
 * state only when a breakpoint is crossed; `null` until the element mounts.
 * Provide it through `ResponsiveBreakpointContext` so every `useResponsive`
 * inside shares the one observer.
 */
export function useElementBreakpoint(element: HTMLElement | null) {
  const [breakpoint, setBreakpoint] = useState<ExtendedBreakpoint | null>(null)

  // Set before paint, so content never shows the window's breakpoint first.
  useLayoutEffect(() => {
    if (!element) return
    setBreakpoint(breakpointForWidth(element.getBoundingClientRect().width))
    const observer = new ResizeObserver(entries => {
      const size = entries[0]?.borderBoxSize[0]
      if (!size) return
      setBreakpoint(breakpointForWidth(size.inlineSize))
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])

  return breakpoint
}

/**
 * @info Responsive hook for detecting viewport breakpoints using Tailwind CSS default theme screens values.
 *
 * This module provides a React hook that detects the current viewport breakpoint
 * and provides boolean flags for each Tailwind CSS breakpoint.
 *
 * @why 🤔 Single hook to detect the current breakpoint and use the boolean flags to conditionally render content or take actions based on the current breakpoint.
 *
 * @description
 * - xs: < 640px (custom breakpoint for very small screens)
 * - sm: 640px
 * - md: 768px
 * - lg: 1024px
 * - xl: 1280px
 * - 2xl: 1536px
 *
 * @usage
 * ```tsx
 * import { useResponsive } from '@mntn/ui-components'
 *
 * function MyComponent() {
 *   const { isMd, isLg, breakpoint, isLessThan } = useResponsive()
 *
 *   return (
 *     <div className={isMd ? 'mobile' : isLg ? 'tablet' : 'desktop'}>
 *       Current breakpoint: {breakpoint}
 *       {isLessThan('md') && <div>Small screen content</div>}
 *     </div>
 *   )
 * }
 * ```
 *
 * @returns
 * * The hook returns the current breakpoint and boolean flags for each breakpoint. ps - See description above for the breakpoints.
 * * isAtLeast() - Will be true if the current breakpoint is at least the given breakpoint and up
 * * isLessThan() - Will be true if the current breakpoint is less than the given breakpoint and down
 * @note Subscribes to window.matchMedia, so it re-renders only when a breakpoint is crossed.
 * Inside a `ResponsiveBreakpointContext`, it returns that breakpoint instead.
 */
export function useResponsive() {
  const containerBreakpoint = useContext(ResponsiveBreakpointContext)
  const windowBreakpoint = useSyncExternalStore(
    containerBreakpoint === null ? subscribeToWindow : subscribeToNothing,
    getWindowBreakpoint,
    getServerBreakpoint,
  )

  return responsiveResults[containerBreakpoint ?? windowBreakpoint]
}
